import {
    makePublicRequest,
    privateAuthenticatorRequest,
    privatePaymentServerRequest
} from './managers/requestBuilder';
import {
    getAccountType,
    getDistributorId,
    getInstallerId,
    getManagementToken,
    getRegion,
    getUid
} from './managers/store';

// billingEmail and customerPrice are EUP only
export const createNewCustomer = async (
    name,
    email,
    reference,
    password,
    billingEmail,
    type,
    cameraQuota,
    analyticsQuota,
    accountLevel,
    annualPaymentInterval,
    customerPrice
) => {
    const { data } = await privateAuthenticatorRequest(
        'POST',
        `/installers/:installerId/subscribers`,
        {
            data: {
                email,
                password,
                name: name || undefined,
                reference: reference || undefined,
                accountLevel,
                cameraQuota,
                analyticsQuota,
                renewalFrequency: annualPaymentInterval ? 12 : 1,
                billingEmail: billingEmail || undefined,
                price: customerPrice,
                subscriberType: type
            },
        },
        true
    );

    return data.uid;
};

/**
 * Fetch customers for the logged in installer manager.
 * Mirrors the old management portal "subscribers" list.
 */
export const getInstallerCustomers = async (
    { search = '', suspended = false } = {}
) => {
    const managementType = getAccountType();
    const installerId = getInstallerId();
    const distributorId = getDistributorId();

    // This legacy endpoint expects both management type and scope.
    const managementScope =
        managementType === 'i'
            ? installerId
            : managementType === 'd'
            ? distributorId
            : 'all';

    const token = getManagementToken();

    // If these values are missing, the request will almost certainly fail silently.
    if (!token || !managementType) {
        // Avoid logging sensitive values. Just record that they're missing.
        console.warn('getInstallerCustomers: missing management credentials', {
            hasToken: !!token,
            managementType
        });
    }

    const { data } = await makePublicRequest('auth', 'GET', '/users', {
        region: getRegion(),
        params: {
            token,
            manageruid: getUid(),
            distributorid: distributorId,
            installerid: installerId,
            search: search || undefined,
            suspended: suspended ? 1 : 0,
            managementScope,
            managementType
        }
    });

    // Support both wrapped ({ result }) and unwrapped responses.
    return data?.result ?? data ?? [];
};

/**
 * Fetch subscription history for a managed customer.
 * Mirrors the legacy /subscriptionhistory/:uid management portal endpoint.
 * @param {number|string} customerUid
 * @returns {Promise<object[]>}
 */
export const getCustomerSubscriptionHistory = async (customerUid) => {
    const managementType = getAccountType();
    const installerId = getInstallerId();
    const distributorId = getDistributorId();

    const managementScope =
        managementType === 'i'
            ? installerId
            : managementType === 'd'
            ? distributorId
            : 'all';

    const token = getManagementToken();

    if (!token || !managementType) {
        console.warn('getCustomerSubscriptionHistory: missing management credentials', {
            hasToken: !!token,
            managementType
        });
    }

    const { data } = await makePublicRequest('auth', 'GET', `/subscriptionhistory/${customerUid}`, {
        region: getRegion(),
        params: {
            token,
            manageruid: getUid(),
            distributorid: distributorId,
            installerid: installerId,
            managementScope,
            managementType
        }
    });

    return data?.result ?? data ?? [];
};

export const getCustomerData = async (
    customerUid
) => {
    const { data } = await privateAuthenticatorRequest(
        'GET',
        `/installers/:installerId/subscribers/${customerUid}`,
        undefined,
        true,
        true
    );

    // 1 year and 2 year plans have an extra day but we don't want to show it
    let cloudStorage = parseInt(data.cloudDays);
    if (cloudStorage > 365 && cloudStorage % 365 === 1) {
        cloudStorage--;
    }

    return {
        name: data.name,
        email: data.email,
        reference: data.customerid,
        billingEmail: data.billingEmail,
        cameraQuota: data.cameraQuota,
        addons: data.analyticsQuota,
        cloudStorage,
        accountLevel: data.accountlevel,
        annualPaymentInterval: data.sub_renewal_frequency_months === 12,
        cancelled: !!data.suspended,
        eupPrice: data.proPrice,
        isEUP: data.paymentType === 'eup',
        maxDevices: data.maxdevices,
        cancellationDate:
            data.cancellationDate && new Date(data.cancellationDate),
        renewalDate: data.renewalDate && new Date(data.renewalDate)
    };
};

// Upgrade fee is cost difference between current and new plans for remainder of payment period
// eupPrice is (obviously) just for EUP and must be undefined/null for invoice installers or number/string for EUP
export const calculateUpgradeFee = async (
    customerUid,
    accountLevel,
    cameraQuota,
    analyticsQuota,
    eupPrice,
    renewalFrequency
) => {
    const { data } = await privatePaymentServerRequest(
        'POST',
        `/installers/:installerId/subscribers/${customerUid}/upgradefee`,
        {
            data: {
                accountLevel,
                cameraQuota,
                analyticsQuota,
                price: eupPrice,
                renewalFrequency
            },
        },
        true,
        true       
    );

    // For invoice installer, data.fees is { installer: <upgrade fee which installer will be invoiced for> }
    // For EUP, data.fees is { installer: <trade price for upgrade>, user: <upgrade fee which end user will be charged> }
    // If no upgrade fee, data.fees should be empty object
    // All prices excluding VAT

    return (
        (typeof eupPrice === 'number' || typeof eupPrice === 'string'
            ? data.fees.user
            : data.fees.installer) ?? 0
    );
};

// Details include name, reference and billingEmail
export const updateCustomerDetails = async (
    customerUid,
    name,
    reference,
    billingEmail
) => {
    await privateAuthenticatorRequest(
        'PATCH',
        `/installers/:installerId/subscribers/${customerUid}`,
        {
            data: {
                // API does not accept empty strings
                name: name || undefined,
                reference: reference || null,
                billingEmail: billingEmail || null,
            },
        },
        true,
        true
    );
};

/**
 * Update customer's subscription.
 * @param {number} customerUid 
 * @param {number} accountLevel 
 * @param {object} cameraQuota e.g. { '2MP': { rom: 1 } }
 * @param {boolean} annualPaymentInterval Use annual payment interval rather than monthly.
 * @param {number} [eupPrice] Price set by EUP installer.
 * @param {boolean} [waiveUpgradeFee] Staff only.
 */
export const updateCustomerPlan = async (
    customerUid,
    accountLevel,
    cameraQuota,
    analyticsQuota,
    annualPaymentInterval,
    eupPrice,
    waiveUpgradeFee
) => {
    await privatePaymentServerRequest(
        'PUT',
        `/installers/:installerId/subscribers/${customerUid}/plan`,
        {
            data: {
                accountLevel,
                cameraQuota,
                analyticsQuota,
                renewalFrequency: annualPaymentInterval ? 12 : 1,
                price: eupPrice,
                withoutUpgradeFee: waiveUpgradeFee
            },
        },
        true,
        true
    );
};

/**
 * Cancel a customer's subscription.
 * @param {number} customerUid uid of customer
 * @param {Date} cancellationDate date (between today and customer's renewal date) cancellation should take effect
 * @param {string} reason reason for cancellation
 * @returns axios promise
 */
export const cancelSubscription = (
    customerUid,
    cancellationDate,
    reason
) => {
    return privatePaymentServerRequest(
        'POST',
        `/installers/:installerId/subscribers/${customerUid}/plan/cancel`,
        {
            data: {
                cancellationDate: cancellationDate.getTime(),
                reason
            }
        },
        true,
        true
    );
};

// Reinstate customer's cancelled subscription
export const reinstateSubscription = async (
    customerUid
) => {
    await privatePaymentServerRequest(
        'POST',
        `/installers/:installerId/subscribers/${customerUid}/plan/reverse-cancellation`,
        undefined,
        true,
        true
    );
};
